import {
  Button,
  Input,
  InputGroup,
  InputLeftContent,
  Loading,
} from "@mozioinc/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { parkingQuery } from "../utils/query";
import { ReactComponent as EmailSvg } from "./assets/email.svg";
import { ReactComponent as ResendSvg } from "./assets/resend-booking.svg";
import { ReactComponent as SuccessSvg } from "./assets/success.svg";
import { ReactComponent as WarningSvg } from "./assets/warning.svg";
import { Dialog } from "./dialog";
import {
  ResendConfirmationButton,
  useCounter,
} from "./resend-confirmation-button";
import type { TransNamespace } from "../types";
import type { ParkingReservation } from "@mozioinc/query";

type ResendConfirmationDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const ResendConfirmationDialog: React.FC<
  ResendConfirmationDialogProps
> = ({ open, onClose }) => {
  const { t } = useTranslation<TransNamespace>(
    "parking:resend-confirmation-dialog",
  );
  const [email, setEmail] = useState("");
  const { counter, startCounter } = useCounter();

  const {
    data,
    isSuccess,
    isLoading: isFetchingReservations,
    refetch: getReservations,
  } = useQuery({
    queryKey: ["reservations", email],
    queryFn: () => parkingQuery.getReservations({ email }),
    select: (reservations) =>
      (reservations as ParkingReservation[]).filter(
        (reservation) => new Date(reservation.arrivalDateTime) > new Date(),
      ),
    enabled: false,
  });

  const resendQueries = useQueries({
    queries: data
      ? data.map((reservation) => ({
          queryKey: ["resendNotification", reservation.id],
          queryFn: () =>
            parkingQuery.resendNotification({ reservationId: reservation.id }),
        }))
      : [],
  });

  const showNotFound = isSuccess && !data?.length;
  const showForm = !isFetchingReservations && !data?.length && !showNotFound;
  const showLoading = isFetchingReservations;
  const showSuccess = !isFetchingReservations && (data?.length ?? 0) > 0;

  const handleSend = (event: React.FormEvent) => {
    event.preventDefault();
    getReservations();
  };

  const handleResend = () => {
    resendQueries.forEach((query) => query.refetch());
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog
      open={open}
      icon={
        <>
          {showForm && <ResendSvg className="text-primary text-6xl" />}
          {showLoading && <Loading className="[&_svg]:h-16 [&_svg]:w-16" />}
          {showSuccess && (
            <div className="bg-success-300 flex h-full w-full items-center justify-center rounded-full">
              <SuccessSvg className="text-8xl text-white" />
            </div>
          )}
          {showNotFound && <WarningSvg className="text-primary text-8xl" />}
        </>
      }
    >
      <h2 className="mb-1 mt-7 text-2xl font-bold leading-9">
        {showForm && t("Resend Confirmation Email")}
        {showLoading && t("Looking for reservations...")}
        {showSuccess &&
          t("{{count}} reservations found", { count: data?.length })}
        {showNotFound && t("No reservations found")}
      </h2>
      <p className="mb-8 text-center">
        {showForm &&
          t(
            "Tell us your email and we'll send you copies of all your upcoming bookings.",
          )}

        {showLoading &&
          t(
            "We're scanning our database for reservations under {{email}} valid starting {{today}}",
            { email, today },
          )}

        {showNotFound &&
          t(
            "There are no reservations under {{email}}, valid starting {{today}}",
            { email, today },
          )}

        {showSuccess &&
          t("Confirmation emails were sent to {{email}}", { email })}
      </p>

      {showForm && (
        <form onSubmit={handleSend} className="w-full text-center">
          <InputGroup className="mb-4">
            <InputLeftContent>
              <EmailSvg />
            </InputLeftContent>
            <Input
              placeholder={t("Email address")}
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </InputGroup>
          <Button type="submit" className="w-full">
            {t("Send")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("Close")}
          </Button>
        </form>
      )}

      {(showNotFound || showSuccess) && (
        <Button onClick={onClose} className="mb-6 w-full">
          {t("OK")}
        </Button>
      )}

      {showSuccess && (
        <ResendConfirmationButton
          onButtonClick={() => {
            startCounter();
            handleResend();
          }}
          countdown={counter}
        />
      )}
    </Dialog>
  );
};
